/* eslint-env browser */
// Settings › Languages (wave 3, W2_Portal_Tables look): the live language
// packs (/languages is the source of truth). The table lists the packs; Add
// and Edit open a dialog (labels above, errors under the field), Delete and
// Make default ask first, and Edit words opens the word list of one pack.
//
// Writes, exactly as the old inline rows wrote them (editLanguage, which
// writes WHOLE language nodes and refuses a node without its pack):
// - Add: { langName, langLocale, dateLocale (when chosen), createdAt,
//   default: false, keyValuePairs } where the words are the default pack
//   machine-translated one key at a time (gettranslation), then 'Add'.
// - Edit: the whole pack with its name / locales changed, 'Update'.
// - Edit words: the whole pack with the new keyValuePairs, 'Update'.
// - Make default: every pack with default true for the chosen one and false
//   for the others, one 'Update' each.
// - Delete: 'Delete' (never the default pack).
// Every write needs critical edits on (settings.AllowCriticalEditsAdmin).
// The page loads EVERY pack once when it opens (it saves whole nodes);
// nothing else is read. The store is no longer changed in place: what this
// page saved is kept here so the table shows it.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import StarOutlineRoundedIcon from '@mui/icons-material/StarOutlineRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { api } from 'common';
import langlocales from '../lists/langlocales';
import datelocales from '../lists/datelocales';
import { FirebaseConfig } from '../config/FirebaseConfig';
import CircularLoading from '../components/CircularLoading';
import { showToast, confirmDialog, InlineMessage } from '../components/feedback';
import { tokens as c } from '../theme';
import { DataTable, RecordDialog, PageHeader, SearchField, StatusChip, calmCaps } from '../components/modern';

// What this page saved, kept for the rest of the session while the store
// still holds the list it started from (EDIT_LANGUAGE does not change it).
let savedCache = null; // { source, data }

const WORDS_PAGE = 150;

// A locale select offers the lists' codes plus the pack's own code when the
// lists do not have it (the live om / ti packs use codes the lists lack), so
// editing a pack never blanks its locale.
const withCurrent = (lookup, value) =>
  value && !Object.prototype.hasOwnProperty.call(lookup, value) ? { [value]: value, ...lookup } : lookup;

const listFor = (source) => {
  if (savedCache && savedCache.source === source) return savedCache.data;
  return Array.isArray(source) ? source : [];
};

// The word list of one pack: search, then edit any key (shown in pages).
function WordsDialog({ open, lang, onClose, onSave, t }) {
  const theme = useTheme();
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [edits, setEdits] = useState({});
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(WORDS_PAGE);
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      setEdits({});
      setQuery('');
      setLimit(WORDS_PAGE);
    }
    wasOpen.current = !!open;
  }, [open]);

  const entries = useMemo(() => (lang && lang.keyValuePairs ? Object.entries(lang.keyValuePairs) : []), [lang]);
  const q = query.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!q) return entries;
    return entries.filter(([k, v]) => k.toLowerCase().includes(q) || String(edits[k] !== undefined ? edits[k] : v).toLowerCase().includes(q));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries, q]);
  const changedCount = Object.keys(edits).filter((k) => edits[k] !== (lang && lang.keyValuePairs ? lang.keyValuePairs[k] : undefined)).length;

  return (
    <Dialog open={!!open} onClose={onClose} fullWidth maxWidth="md" fullScreen={phone} aria-labelledby="lang-words-title">
      <DialogTitle id="lang-words-title" sx={{ pr: 8 }}>
        {t('language_words_title', { name: lang ? lang.langName : '', defaultValue: 'Words: {{name}}' })}
        <IconButton aria-label={t('close_dialog', { defaultValue: 'Close' })} onClick={onClose} sx={{ position: 'absolute', right: 12, top: 12 }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
          <SearchField value={query} onChange={(v) => { setQuery(v); setLimit(WORDS_PAGE); }} placeholder={t('language_words_search', { defaultValue: 'Search key or words' })} width={{ xs: '100%', sm: 320 }} />
          <Typography variant="body2" role="status" sx={{ color: c.TEXT_3 }}>
            {t('table_count_shown', { shown: matches.length, total: entries.length, defaultValue: '{{shown}} of {{total}}' })}
          </Typography>
        </Box>
        {matches.slice(0, limit).map(([k, v]) => {
          const id = `lang-word-${k}`;
          const value = edits[k] !== undefined ? edits[k] : v;
          return (
            <Box key={k} sx={{ minWidth: 0 }}>
              <Typography component="label" htmlFor={id} sx={{ display: 'block', fontSize: 13, fontWeight: 600, color: c.INK_2, mb: 0.5, overflowWrap: 'anywhere' }}>
                {k}
              </Typography>
              <TextField
                id={id}
                size="small"
                fullWidth
                multiline
                maxRows={6}
                value={value === undefined || value === null ? '' : value}
                onChange={(e) => setEdits((prev) => ({ ...prev, [k]: e.target.value }))}
              />
            </Box>
          );
        })}
        {matches.length > limit ? (
          <Button variant="outlined" color="neutral" onClick={() => setLimit((n) => n + WORDS_PAGE)} sx={{ alignSelf: 'center' }}>
            {t('load_more', { defaultValue: 'Load more' })}
          </Button>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Typography variant="body2" sx={{ color: c.INK_2, mr: 'auto', pl: 1 }}>
          {changedCount === 1
            ? t('settings_unsaved_one', { defaultValue: '1 unsaved change' })
            : changedCount > 1
              ? t('settings_unsaved_many', { n: changedCount, defaultValue: '{{n}} unsaved changes' })
              : ''}
        </Typography>
        <Button variant="text" color="neutral" onClick={onClose}>
          {t('cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={changedCount === 0}
          onClick={() => {
            // The same object the old editor built: every key in the pack's
            // order, with the edited words.
            const obj = {};
            entries.forEach(([k, v]) => {
              obj[k] = edits[k] !== undefined ? edits[k] : v;
            });
            onSave(obj);
          }}
        >
          {t('save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function LanguageSetting() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { editLanguage } = api;
  const languagedata = useSelector((state) => state.languagedata);
  const settings = useSelector((state) => state.settingsdata.settings);
  const canEdit = !!(settings && settings.AllowCriticalEditsAdmin);

  // This editor saves WHOLE language nodes, so it needs every pack. The app
  // normally holds only the default + the user's language: load the full set
  // on entry and show nothing editable until it is here (editLanguage also
  // refuses a node without its pack, as a second guard).
  useEffect(() => {
    dispatch(api.fetchLanguages({ full: true }));
  }, [dispatch]);
  const fullyLoaded = languagedata.full === true;
  const source = fullyLoaded ? languagedata.langlist : null;

  const [data, setData] = useState(() => listFor(source));
  useEffect(() => {
    setData(listFor(source));
  }, [source]);

  const [editing, setEditing] = useState(null); // {} = add, { row } = edit
  const [wordsRow, setWordsRow] = useState(null);
  const [completed, setCompleted] = useState(0); // translation progress of an Add, %
  const translating = completed > 0 && completed < 100;

  const remember = (next) => {
    savedCache = { source, data: next };
    setData(next);
  };

  const demo = () => showToast.warning(t('demo_mode'));

  const handleMakeDefault = async (rowData) => {
    if (!canEdit) {
      demo();
      return;
    }
    const ok = await confirmDialog({
      title: t('language_default_title', { name: rowData.langName, defaultValue: 'Make {{name}} the default language?' }),
      body: t('language_default_body', { defaultValue: 'The apps and the portal fall back to the default language when a word has no translation.' }),
      confirmLabel: t('make_default'),
    });
    if (!ok) return;
    const curVal = rowData['default'];
    const next = data.map((value) => {
      const updated = { ...value, default: rowData.id === value.id ? !curVal : curVal };
      dispatch(editLanguage(updated, 'Update'));
      return updated;
    });
    remember(next);
    showToast.success(t('saved', { defaultValue: 'Saved' }));
  };

  const handleSaveWords = (obj) => {
    const row = wordsRow;
    try {
      if (!canEdit) {
        demo();
        return;
      }
      const index = data.indexOf(row);
      const updated = { ...row, keyValuePairs: obj };
      dispatch(editLanguage(updated, 'Update'));
      if (index >= 0) {
        const next = data.slice();
        next[index] = updated;
        remember(next);
      }
      setWordsRow(null);
      showToast.success(t('saved', { defaultValue: 'Saved' }));
    } catch (error) {
      showToast.error(t('language_json_error', { defaultValue: 'The words were not saved because the language data is not valid JSON.' }));
    }
  };

  const handleSave = async (values, { mode, initialValues, changed }) => {
    if (!canEdit) {
      demo();
      return;
    }
    if (mode === 'add') {
      // The words of the default pack, machine-translated one key at a time
      // (the same calls, in the same order, as before).
      let kvSet = {};
      for (const value of Object.values(data)) {
        if (value.default) {
          kvSet = value.keyValuePairs;
        }
      }
      const newData = { ...values };
      newData['createdAt'] = new Date().getTime();
      newData['default'] = false;
      setEditing(null);
      setCompleted(1);
      const keys = Object.keys(kvSet);
      const obj = {};
      for (let i = 0; i < keys.length; i++) {
        try {
          const url = FirebaseConfig.databaseURL.split('.').length === 4 ? FirebaseConfig.databaseURL.split('.')[1] : 'us-central1';
          const response = await fetch(`https://${url}-${FirebaseConfig.projectId}.cloudfunctions.net/gettranslation?str=${kvSet[keys[i]]}&from=en&to=${newData.langLocale}`, {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          });
          const json = await response.json();
          obj[keys[i]] = json.text;
        } catch (err) {
          obj[keys[i]] = kvSet[keys[i]];
        }
        setCompleted(Math.max(1, parseInt(((i + 1) / keys.length) * 100)));
      }
      newData['keyValuePairs'] = obj;
      dispatch(editLanguage(newData, 'Add'));
      setCompleted(0);
      showToast.success(t('language_added', { defaultValue: 'Language added. Open this tab again to see it in the list.' }));
      return;
    }
    // An edit that changes nothing writes nothing.
    if (!changed.length) return;
    const index = data.indexOf(initialValues);
    if (index < 0) throw new Error(t('cancel_reasons_changed', { defaultValue: 'The list changed. Close this and try again.' }));
    dispatch(editLanguage(values, 'Update'));
    const next = data.slice();
    next[index] = values;
    remember(next);
    showToast.success(t('saved', { defaultValue: 'Saved' }));
  };

  const handleDelete = async (row) => {
    if (!canEdit) {
      demo();
      return;
    }
    if (row.default) {
      showToast.error(t('cannot_delete_default_language', { defaultValue: 'The default language cannot be deleted.' }));
      return;
    }
    const ok = await confirmDialog({
      title: t('language_delete_title', { name: row.langName, defaultValue: 'Delete the language {{name}}?' }),
      body: t('language_delete_body', { defaultValue: 'Its words are deleted for everyone using the apps and the portal. This cannot be undone.' }),
      confirmLabel: t('delete'),
      cancelLabel: t('confirm_cancel', { defaultValue: 'Cancel' }),
      destructive: true,
    });
    if (!ok) return;
    dispatch(editLanguage(row, 'Delete'));
    remember(data.filter((item) => item !== row));
    showToast.success(t('language_deleted', { defaultValue: 'Language deleted' }));
  };

  const title = calmCaps(t('language_cap', { defaultValue: 'Languages' }));

  const columns = [
    {
      title: t('langName'),
      field: 'langName',
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box component="span" sx={{ fontWeight: 600 }}>{row.langName}</Box>
          {row.default ? <StatusChip tone="accent" icon={<StarRoundedIcon />} label={t('language_default', { defaultValue: 'Default' })} /> : null}
        </Box>
      ),
    },
    { title: t('langLocale'), field: 'langLocale', lookup: langlocales },
    { title: calmCaps(t('dateLocale')), field: 'dateLocale', lookup: datelocales },
    {
      title: t('language_words', { defaultValue: 'Words' }),
      id: 'words',
      type: 'number',
      sortValue: (row) => (row.keyValuePairs ? Object.keys(row.keyValuePairs).length : 0),
      render: (row) => (row.keyValuePairs ? Object.keys(row.keyValuePairs).length.toLocaleString('en-US') : '—'),
      export: false,
    },
  ];

  const actions = [
    { key: 'words', label: t('language_edit_words', { defaultValue: 'Edit words' }), icon: <TranslateRoundedIcon />, inline: true, onClick: (row) => setWordsRow(row), disabled: () => translating },
    { key: 'edit', label: t('edit'), icon: <EditOutlinedIcon />, inline: true, onClick: (row) => setEditing({ row }), disabled: () => translating },
    {
      key: 'default',
      label: t('make_default'),
      icon: <StarOutlineRoundedIcon />,
      disabled: (row) => !!row.default || translating,
      onClick: handleMakeDefault,
    },
    {
      key: 'delete',
      label: t('language_delete', { defaultValue: 'Delete language' }),
      icon: <DeleteOutlineRoundedIcon />,
      destructive: true,
      disabled: (row) => !!row.default || translating,
      onClick: handleDelete,
    },
  ];

  if (!fullyLoaded && !languagedata.loading && languagedata.error && languagedata.error.flag) {
    return (
      <InlineMessage kind="error" title={t('no_internet_connection')} actionLabel={t('retry')} onAction={() => dispatch(api.fetchLanguages({ full: true }))}>
        {t('load_failed_body', { defaultValue: 'Check the internet connection, then try again.' })}
      </InlineMessage>
    );
  }
  if (languagedata.loading || !fullyLoaded) return <CircularLoading inline />;

  const isEdit = !!(editing && editing.row);
  const addLabel = t('language_add', { defaultValue: 'Add language' });

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <PageHeader
        primaryAction={{ label: addLabel, icon: <AddRoundedIcon />, onClick: () => (canEdit ? setEditing({}) : demo()), disabled: translating }}
      />
      {translating ? (
        <InlineMessage kind="info" icon={<CircularProgress size={18} color="inherit" />} title={t('language_translating', { defaultValue: 'Translating the words…' })}>
          <LinearProgress variant="determinate" value={completed} sx={{ mt: 1, height: 6, borderRadius: '3px' }} aria-label={t('language_translating', { defaultValue: 'Translating the words…' })} />
          <Box sx={{ mt: 0.5, fontVariantNumeric: 'tabular-nums' }}>{`${completed}% ${t('complete', { defaultValue: 'Complete' })}`}</Box>
        </InlineMessage>
      ) : null}
      <DataTable
        title={title}
        rows={data}
        columns={columns}
        rowKey={(row, index) => row.id || `new-${index}`}
        countText={({ total, query, shown }) =>
          query
            ? t('table_count_shown', { shown, total, defaultValue: '{{shown}} of {{total}}' })
            : t('language_count', { count: total, defaultValue_one: '{{count}} language', defaultValue_other: '{{count}} languages', defaultValue: '{{count}} languages' })
        }
        rowLabel={(row) => row.langName}
        actions={actions}
        searchPlaceholder={t('language_search', { defaultValue: 'Search languages' })}
        prefsKey="languages"
        exportCsv={canEdit}
        exportColumns={[
          { title: t('langName'), field: 'langName' },
          { title: t('langLocale'), field: 'langLocale' },
          { title: t('dateLocale'), field: 'dateLocale' },
        ]}
        exportFileName="languages"
        exportTitle={title}
        minWidth={640}
        emptyState={({ query }) => (query ? null : { icon: <TranslateRoundedIcon />, title: t('language_empty', { defaultValue: 'No languages yet' }) })}
      />
      <RecordDialog
        open={!!editing}
        mode={isEdit ? 'edit' : 'add'}
        title={isEdit ? t('language_edit', { defaultValue: 'Edit language' }) : addLabel}
        description={isEdit ? undefined : t('language_add_hint', { defaultValue: 'The words of the default language are machine-translated into the new language when you save.' })}
        fields={[
          { name: 'langName', label: t('langName'), required: true, autoFocus: true, maxLength: 60 },
          { name: 'langLocale', label: t('langLocale'), type: 'select', required: true, lookup: withCurrent(langlocales, isEdit ? editing.row.langLocale : null), width: 'half' },
          { name: 'dateLocale', label: calmCaps(t('dateLocale')), type: 'select', lookup: withCurrent(datelocales, isEdit ? editing.row.dateLocale : null), width: 'half' },
        ]}
        initialValues={isEdit ? editing.row : undefined}
        submitLabel={isEdit ? undefined : addLabel}
        onSubmit={handleSave}
        onClose={() => setEditing(null)}
      />
      <WordsDialog open={!!wordsRow} lang={wordsRow} onClose={() => setWordsRow(null)} onSave={handleSaveWords} t={t} />
    </Box>
  );
}
